// TAF raw → plain-text translator.
//
// AWC's /api/data/taf returns a structured envelope per station:
//
//   icaoId, validTimeFrom, validTimeTo, issueTime, rawTAF,
//   fcsts: [
//     { timeFrom, timeTo, fcstChange, probability, wdir, wspd, wgst,
//       visib, altim, vertVis, wxString, clouds, icgTurb, temp }
//     ...
//   ]
//
// Each `fcsts` entry is one forecast period. We render each as a
// human-readable narrative; the AirportDetail TAF panel concatenates
// them in order so the pilot sees the evolving forecast in plain
// English.

import { clockTimeFormatOptions } from './timeFormat';

export interface TafForecastPeriod {
  timeFrom?: number;       // epoch seconds (AWC convention)
  timeTo?: number;
  timeBec?: number | null;
  fcstChange?: string | null;   // 'BECMG' | 'TEMPO' | 'FROM' | 'PROB30' | 'PROB40' | etc.
  probability?: number | null;
  wdir?: number | string | null;
  wspd?: number | null;
  wgst?: number | null;
  wshearHgt?: number | null;
  wshearDir?: number | null;
  wshearSpd?: number | null;
  visib?: number | string | null;
  altim?: number | null;
  vertVis?: number | null;
  wxString?: string | null;
  notDecoded?: string | null;
  clouds?: Array<{ cover?: string; base?: number | null; type?: string | null }>;
  icgTurb?: any[];
}

export interface TafEnvelope {
  icaoId?: string;
  validTimeFrom?: number;
  validTimeTo?: number;
  issueTime?: string;
  rawTAF?: string;
  fcsts?: TafForecastPeriod[];
}

/** Render a single TAF forecast period as a one-paragraph narrative. */
export function plainTextTafPeriod(p: TafForecastPeriod): string {
  const parts: string[] = [];

  // Lead with the change-type + window.
  const window = humanWindow(p.timeFrom, p.timeTo);
  const change = humanChangeIndicator(p.fcstChange, p.probability);
  if (change && window) parts.push(`${change}, ${window}:`);
  else if (change) parts.push(`${change}:`);
  else if (window) parts.push(`${window}:`);

  // Wind.
  const wind = humanWind(p.wdir, p.wspd, p.wgst);
  if (wind) parts.push(wind + '.');
  // Wind shear.
  if (p.wshearHgt != null && p.wshearDir != null && p.wshearSpd != null) {
    parts.push(`Wind shear at ${(p.wshearHgt).toLocaleString()} ft: ${String(p.wshearDir).padStart(3, '0')}\u00b0 at ${p.wshearSpd} kt.`);
  }
  // Visibility.
  const vis = humanVis(p.visib);
  if (vis) parts.push(vis + '.');
  // Wx phenomena.
  if (p.wxString) parts.push(`${humanWxString(p.wxString)}.`);
  // Sky.
  const sky = humanSky(p.clouds, p.vertVis);
  if (sky) parts.push(sky + '.');

  return parts.join(' ').trim();
}

/** Render a full TAF as a stack of narratives (one per period). */
export function plainTextTaf(taf: TafEnvelope): string[] {
  const fcsts = Array.isArray(taf.fcsts) ? taf.fcsts : [];
  return fcsts.map(plainTextTafPeriod).filter(Boolean);
}

function humanWindow(fromS?: number, toS?: number): string | null {
  if (fromS == null || toS == null) return null;
  const from = new Date(fromS * 1000);
  const to = new Date(toS * 1000);
  const fromStr = `${pad2(from.getUTCDate())} ${pad2(from.getUTCHours())}${pad2(from.getUTCMinutes())}Z`;
  const toStr = `${pad2(to.getUTCDate())} ${pad2(to.getUTCHours())}${pad2(to.getUTCMinutes())}Z`;
  // Local times in parens so a pilot doesn't have to mental-math UTC
  // offsets. Browser's locale picks the zone abbrev (PST/PDT/...).
  const fromLocal = formatLocalShort(from);
  const toLocal = formatLocalShort(to);
  const localPart = (fromLocal && toLocal) ? ` (${fromLocal} → ${toLocal})` : '';
  return `from ${fromStr} to ${toStr}${localPart}`;
}

function formatLocalShort(d: Date): string {
  try {
    // Use the user's preferred 24h/12h clock. timeFormat owns the
    // hour-shape options; we just append a short timezone name.
    return new Intl.DateTimeFormat(undefined, {
      ...clockTimeFormatOptions(),
      timeZoneName: 'short',
    }).format(d);
  } catch {
    return '';
  }
}

function humanChangeIndicator(code: string | null | undefined, prob: number | null | undefined): string | null {
  if (!code) {
    if (prob != null) return `${prob}% probability`;
    return null;
  }
  const c = code.toUpperCase();
  if (c.startsWith('BECMG')) return 'Becoming';
  if (c.startsWith('TEMPO')) return 'Temporarily';
  if (c.startsWith('FROM') || c === 'FM') return 'From';
  if (c.startsWith('PROB')) {
    const m = c.match(/PROB(\d+)/);
    if (m) return `${m[1]}% probability`;
    return 'Probable';
  }
  return c;
}

function humanWind(dir: number | string | null | undefined, spd: number | null | undefined, gst: number | null | undefined): string | null {
  if (spd == null) return null;
  let dirStr = '';
  if (dir == null || dir === '' || (typeof dir === 'string' && dir.toUpperCase() === 'VRB')) {
    dirStr = 'Variable';
  } else {
    const d = typeof dir === 'number' ? dir : parseInt(dir as string, 10);
    if (Number.isFinite(d)) dirStr = `${String(d).padStart(3, '0')}\u00b0`;
    else dirStr = String(dir);
  }
  const baseSpd = spd === 0 ? 'calm' : `${spd} kt`;
  if (spd === 0) return 'Wind calm';
  if (gst != null) return `Wind ${dirStr} at ${baseSpd} gusting ${gst} kt`;
  return `Wind ${dirStr} at ${baseSpd}`;
}

function humanVis(v: number | string | null | undefined): string | null {
  if (v == null) return null;
  if (typeof v === 'number') return `Visibility ${v} sm`;
  const t = String(v).trim();
  if (!t) return null;
  if (t === '6+' || t === '10+' || t.endsWith('+')) {
    const n = t.replace('+', '');
    return `Visibility ${n} sm or greater`;
  }
  return `Visibility ${t} sm`;
}

function humanWxString(wx: string): string {
  // Common METAR/TAF wx codes \u2014 expand into words.
  const tokens = wx.trim().split(/\s+/);
  const out: string[] = [];
  for (const tok of tokens) out.push(expandWxToken(tok));
  return out.join(', ');
}

function expandWxToken(tok: string): string {
  // Intensity prefix: -, +, VC
  let intensity = '';
  let body = tok;
  if (body.startsWith('-')) { intensity = 'Light '; body = body.slice(1); }
  else if (body.startsWith('+')) { intensity = 'Heavy '; body = body.slice(1); }
  else if (body.startsWith('VC')) { intensity = 'In the vicinity '; body = body.slice(2); }
  // Descriptor + phenomena \u2014 split by 2-char chunks.
  const map: Record<string, string> = {
    MI: 'shallow', PR: 'partial', BC: 'patchy', DR: 'low drifting', BL: 'blowing',
    SH: 'showers', TS: 'thunderstorm', FZ: 'freezing',
    DZ: 'drizzle', RA: 'rain', SN: 'snow', SG: 'snow grains', IC: 'ice crystals',
    PL: 'ice pellets', GR: 'hail', GS: 'small hail',
    BR: 'mist', FG: 'fog', FU: 'smoke', VA: 'volcanic ash', DU: 'widespread dust',
    SA: 'sand', HZ: 'haze', PY: 'spray',
    PO: 'dust whirl', SQ: 'squall', FC: 'funnel cloud', SS: 'sandstorm', DS: 'duststorm',
    UP: 'unknown precip',
  };
  const pieces: string[] = [];
  for (let i = 0; i + 2 <= body.length; i += 2) {
    const chunk = body.slice(i, i + 2);
    if (map[chunk]) pieces.push(map[chunk]);
    else if (i === 0 && /^[A-Z]+$/.test(body)) { pieces.push(body); break; }
    else pieces.push(chunk);
  }
  if (pieces.length === 0) return intensity + body;
  return (intensity + pieces.join(' ')).trim();
}

function humanSky(
  clouds: Array<{ cover?: string; base?: number | null; type?: string | null }> | null | undefined,
  vertVis: number | null | undefined,
): string | null {
  if (vertVis != null) return `Vertical visibility ${vertVis} ft`;
  if (!Array.isArray(clouds) || clouds.length === 0) return null;
  const layers = clouds.map((c) => {
    const cov = (c.cover || '').toUpperCase();
    const cm: Record<string, string> = {
      SKC: 'sky clear', CLR: 'clear', NSC: 'no significant cloud',
      FEW: 'few clouds', SCT: 'scattered', BKN: 'broken', OVC: 'overcast', OVX: 'sky obscured',
    };
    const phrase = cm[cov] || cov.toLowerCase();
    if (cov === 'SKC' || cov === 'CLR' || cov === 'NSC' || cov === 'OVX') return phrase;
    if (c.base == null) return phrase;
    const baseFt = c.base.toLocaleString();
    const cb = (c.type || '').toUpperCase();
    const cbStr = cb === 'CB' ? ' cumulonimbus' : cb === 'TCU' ? ' towering cumulus' : '';
    return `${phrase} at ${baseFt} ft${cbStr}`;
  });
  return layers.join(', ');
}

function pad2(n: number): string { return String(n).padStart(2, '0'); }
